"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Device = void 0;
var Layer_1 = require("./Layer");
var createDOMCanvas_1 = require("./dom/createDOMCanvas");
var Transform_1 = require("./Transform");
/**
 * Represents the graphics rendering device. Could be a web browser or a Node
 * server environment.
 */
var Device = /** @class */ (function () {
    /**
     * @param createCanvas Callback for creating a new, blank canvas.
     * @param canvasSurfaceLimit Canvas limit to assert if Paintable can paint
     * @param pixelRatio See {@link https://developer.mozilla.org/docs/Web/API/Window/devicePixelRatio}
     * @param transform Used to transform the coordinate system to screen space.
     * @param paintSurfaceLimit Paint limit to assert if Paintable can paint. Certain environments
     * do not support draw operations larger than their max canvas surface like node-canvas
     */
    function Device(createCanvas, canvasSurfaceLimit, pixelRatio, transform, paintSurfaceLimit) {
        if (pixelRatio === void 0) { pixelRatio = 1; }
        if (transform === void 0) { transform = Transform_1.Transform.none(); }
        this.createCanvas = createCanvas;
        this.canvasSurfaceLimit = canvasSurfaceLimit;
        this.pixelRatio = pixelRatio;
        this.transform = transform;
        this.paintSurfaceLimit = paintSurfaceLimit;
    }
    // Maintenance notice:
    //
    // This object gets passed way down the pipeline, so despite its generic name,
    // it should be kept small and certainly not keep any mutable state.
    /**
     * Shortcut to construct a browser-based device.
     *
     * @param canvasSurfaceLimit Canvas limit to assert if Paintable can paint
     * @param transform Used to transform the coordinate system to screen space.
     */
    Device.browser = function (canvasSurfaceLimit, transform) {
        return new Device(createDOMCanvas_1.createDOMCanvas, canvasSurfaceLimit, window.devicePixelRatio || 1, transform);
    };
    /**
     * Creates a drawing surface.
     */
    Device.prototype.createLayer = function (canvasType) {
        if (canvasType === void 0) { canvasType = "auto"; }
        return new Layer_1.Layer(this, canvasType);
    };
    /**
     * @see transform
     */
    Device.prototype.scale = function (ctx) {
        ctx.scale(this.transform.scale.x, this.transform.scale.y);
    };
    /**
     * @see transform
     */
    Device.prototype.undoScale = function (ctx) {
        ctx.scale(1 / this.transform.scale.x, 1 / this.transform.scale.y);
    };
    /**
     * @see transform
     */
    Device.prototype.translate = function (ctx) {
        ctx.translate(this.transform.translate.x, this.transform.translate.y);
    };
    /**
     * @see transform
     */
    Device.prototype.rotate = function (ctx) {
        this.transform.angle && ctx.rotate(this.transform.angle);
    };
    /**
     * Returns the size scaled by the pixel ratio and transform.
     */
    Device.prototype.toVirtualRenderResolution = function (size) {
        var result = {
            width: Math.ceil(size.width * this.transform.scale.x * this.pixelRatio),
            height: Math.ceil(size.height * this.transform.scale.y * this.pixelRatio)
        };
        var limit = this._getMinSurfaceLimit();
        var isOversize = result.width >= limit.width
            || result.height >= limit.height
            || result.width * result.height >= limit.area;
        if (isOversize) {
            var ratio = size.width / size.height;
            var newWidth = void 0, newHeight = void 0;
            if (size.width < size.height) {
                newHeight = limit.height - 1;
                newWidth = ratio * newHeight;
            }
            else {
                newWidth = limit.width - 1;
                newHeight = newWidth / ratio;
            }
            if (newWidth * newHeight >= limit.area) {
                newWidth = Math.sqrt((limit.area - 1) / ratio);
                newHeight = newWidth / ratio;
            }
            return {
                width: Math.ceil(newWidth),
                height: Math.ceil(newHeight)
            };
        }
        else {
            return result;
        }
    };
    Device.prototype._getMinSurfaceLimit = function () {
        if (this.paintSurfaceLimit) {
            return {
                width: Math.min(this.canvasSurfaceLimit.width, this.paintSurfaceLimit.width),
                height: Math.min(this.canvasSurfaceLimit.height, this.paintSurfaceLimit.height),
                area: Math.min(this.canvasSurfaceLimit.area, this.paintSurfaceLimit.area)
            };
        }
        else {
            return this.canvasSurfaceLimit;
        }
    };
    return Device;
}());
exports.Device = Device;
//# sourceMappingURL=Device.js.map